/**
 * Sample average colour from the left edge of an image (for hero blend sync).
 */
export function sampleImageEdgeColor(imageSrc) {
  return new Promise((resolve) => {
    if (!imageSrc) {
      resolve({ rgb: '255, 245, 240', hex: '#fff5f0', luminance: 0.92 });
      return;
    }

    const img = new Image();
    if (/^https?:\/\//i.test(imageSrc)) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const sw = Math.min(48, img.naturalWidth || img.width);
        const sh = Math.min(48, img.naturalHeight || img.height);
        canvas.width = sw;
        canvas.height = sh;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const sx = 0;
        const sy = (img.naturalHeight || img.height) * 0.25;
        const sWidth = Math.min(80, (img.naturalWidth || img.width) * 0.12);
        const sHeight = (img.naturalHeight || img.height) * 0.5;

        ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, sw, sh);
        const { data } = ctx.getImageData(0, 0, sw, sh);

        let r = 0;
        let g = 0;
        let b = 0;
        let n = 0;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          n += 1;
        }
        const R = Math.round(r / n);
        const G = Math.round(g / n);
        const B = Math.round(b / n);
        resolve({
          rgb: `${R}, ${G}, ${B}`,
          hex: rgbToHex(R, G, B),
          luminance: relativeLuminance(R, G, B),
        });
      } catch {
        resolve({ rgb: '255, 245, 240', hex: '#fff5f0', luminance: 0.92 });
      }
    };

    img.onerror = () => resolve({ rgb: '255, 245, 240', hex: '#fff5f0', luminance: 0.92 });
    img.src = imageSrc;
  });
}

/** Sample the top band of an image — used for navbar contrast over hero. */
export function sampleImageTopTone(imageSrc) {
  return new Promise((resolve) => {
    if (!imageSrc) {
      resolve({ tone: 'dark', luminance: 0.85 });
      return;
    }

    const img = new Image();
    if (/^https?:\/\//i.test(imageSrc)) img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 24;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h * 0.18, 0, 0, 64, 24);
        const { data } = ctx.getImageData(0, 0, 64, 24);
        let r = 0;
        let g = 0;
        let b = 0;
        let n = 0;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          n += 1;
        }
        const lum = relativeLuminance(r / n, g / n, b / n);
        resolve({ tone: lum < 0.48 ? 'light' : 'dark', luminance: lum });
      } catch {
        resolve({ tone: 'dark', luminance: 0.85 });
      }
    };

    img.onerror = () => resolve({ tone: 'dark', luminance: 0.85 });
    img.src = imageSrc;
  });
}

export function relativeLuminance(r, g, b) {
  const toLin = (c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

export default sampleImageEdgeColor;
